import { useMemo } from 'react';
import { UserRankingDto } from '@repo/shared/dtos/analytics/UtilizationDto';
import { Zap, Moon, Star } from 'lucide-react';
import { formatNumber } from '../../utils/dataUtils';
import {
  TIER_LABEL_CHAMPION,
  TIER_LABEL_POWER,
  TIER_LABEL_INACTIVE,
  TIER_DESC_CHAMPION,
  TIER_DESC_POWER,
  TIER_DESC_INACTIVE,
} from '../constants';
import { isChampion, isPowerMember, isInactive } from '../utils/tierUtils';

export function avgSessionsPerMonth(user: UserRankingDto): number {
  return user.avg_sessions_per_month;
}

interface InsightKpiRowProps {
  data: UserRankingDto[];
}

export function InsightKpiRow({ data }: InsightKpiRowProps) {
  const championCount = useMemo(() => data.filter(u => isChampion(u)).length, [data]);
  const powerCount = useMemo(() => data.filter(u => isPowerMember(u)).length, [data]);
  const inactiveCount = useMemo(() => data.filter(u => isInactive(u)).length, [data]);

  return (
    <div className="mb-5 grid grid-cols-3 gap-3">
      <div className="flex flex-col items-center gap-1 rounded-lg border border-yellow-200 bg-yellow-50 px-2 py-2 sm:px-4 sm:py-3 dark:border-yellow-800 dark:bg-yellow-900/20">
        <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-yellow-700 dark:text-yellow-400">
          <Star className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          <span className="hidden sm:inline">{TIER_LABEL_CHAMPION}</span>
          <span className="sm:hidden">Champ</span>
        </div>
        <span className="text-lg font-bold text-yellow-800 sm:text-2xl dark:text-yellow-300">
          {formatNumber(championCount)}
        </span>
        <span className="hidden text-center text-xs text-yellow-600 sm:block dark:text-yellow-500">{TIER_DESC_CHAMPION}</span>
      </div>

      <div className="flex flex-col items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-2 sm:px-4 sm:py-3 dark:border-emerald-800 dark:bg-emerald-900/20">
        <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
          <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          {TIER_LABEL_POWER}
        </div>
        <span className="text-lg font-bold text-emerald-800 sm:text-2xl dark:text-emerald-300">
          {formatNumber(powerCount)}
        </span>
        <span className="hidden text-center text-xs text-emerald-600 sm:block dark:text-emerald-500">{TIER_DESC_POWER}</span>
      </div>

      <div className="flex flex-col items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-2 sm:px-4 sm:py-3 dark:border-amber-800 dark:bg-amber-900/20">
        <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
          <Moon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          {TIER_LABEL_INACTIVE}
        </div>
        <span className="text-lg font-bold text-amber-800 sm:text-2xl dark:text-amber-300">
          {formatNumber(inactiveCount)}
        </span>
        <span className="hidden text-center text-xs text-amber-600 sm:block dark:text-amber-500">{TIER_DESC_INACTIVE}</span>
      </div>
    </div>
  );
}
